import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Form, Alert } from 'react-bootstrap';
import { Button, CircularProgress } from '@mui/material';
import { DateRange } from '@mui/icons-material';
import { FaCalendarAlt, FaUser, FaFilePdf, FaFileExcel, FaPrint } from 'react-icons/fa';
import { ToastContainer, toast } from "react-toastify";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from 'xlsx';
import fetchData from '../../functions/fetchData';
import useBillingDate from '../../hooks/useBillingDate';
import Layout from '../../layout/Layout';
import logo from '../../assets/logo.png';

export default function CashierSummaryReport() {
  const { billingDate } = useBillingDate();
  const [selectedDate, setSelectedDate] = useState('');
  const [cashierSummaries, setCashierSummaries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initialize selectedDate with billing date when available
  useEffect(() => {
    if (billingDate && !selectedDate) {
      setSelectedDate(billingDate);
    }
  }, [billingDate, selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      fetchCashierSummaries();
    }
  }, [selectedDate]);

  const fetchCashierSummaries = async () => {
    if (!selectedDate) return;
    
    setLoading(true);
    try {
     // console.log('Fetching cashier summaries for date:', selectedDate);
      
      // Fetch cashier summaries for the selected date
      const summaries = await fetchData("cashier_summaries", null, "id", { setup_date: selectedDate });
     // console.log('Cashier summaries data:', summaries);
      
      if (summaries && summaries.length > 0) {
        // Fetch users data to get cashier names by matching uname with cashier_id
        // Pass empty object to fetch all users
        const users = await fetchData("users", null, "id", {});
      //  console.log('Users data fetched:', users);
        
        const usersMap = {};
        if (users && Array.isArray(users)) {
          users.forEach(user => {
            // Map by uname since cashier_id stores uname (like "4229", "97793")
            usersMap[user.uname] = { name: user.name, uname: user.uname };
          });
         // console.log('Users map created:', usersMap);
        }
        
        // Enrich summaries with actual cashier name from users table
        const enrichedSummaries = summaries.map(summary => {
          const userInfo = usersMap[summary.cashier_id] || {};
         // console.log(`Mapping cashier_id ${summary.cashier_id} to user:`, userInfo);
          return {
            ...summary,
            cashier_display: userInfo.name || summary.cashier_name || 'N/A',
            cashier_uname: summary.cashier_id || 'N/A'
          };
        });
        
       // console.log('Enriched summaries:', enrichedSummaries);
        setCashierSummaries(enrichedSummaries);
      } else {
        setCashierSummaries([]);
        toast.info('No cashier summaries found for the selected date');
      }
    } catch (error) {
      console.error('Error fetching cashier summaries:', error);
      toast.error('Error fetching cashier summary data');
      setCashierSummaries([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `฿ ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMinutes = Math.round((end - start) / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const calculateColumnTotals = () => {
    const totals = {
      totalSales: 0,
      totalTransactions: 0,
      totalCash: 0,
      totalUpi: 0,
      totalCard: 0,
      totalQr: 0,
      totalNonCash: 0,
      totalItemsSold: 0,
      totalSessions: cashierSummaries.length
    };

    cashierSummaries.forEach(summary => {
      totals.totalSales += parseFloat(summary.total_sales || 0);
      totals.totalTransactions += parseInt(summary.total_transactions || 0);
      totals.totalCash += parseFloat(summary.cash_total || 0);
      totals.totalUpi += parseFloat(summary.upi_total || 0);
      totals.totalCard += parseFloat(summary.card_total || 0);
      totals.totalQr += parseFloat(summary.qr_total || 0);
      totals.totalNonCash += parseFloat(summary.non_cash_total || 0);
      totals.totalItemsSold += parseInt(summary.total_items_sold || 0);
    });

    return totals;
  };

  // Export to PDF using jsPDF
  const exportToPDF = () => {
    const totals = calculateColumnTotals();
    const doc = new jsPDF('landscape'); // Use landscape for better table fit
    
    // Add logo
    try {
      doc.addImage(logo, "PNG", 250, 10, 40, 15);
    } catch (error) {
      console.log('Logo not available');
    }
    
    doc.setFontSize(16);
    doc.text("Cashier Summary Report", 14, 20);
    
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(selectedDate).toLocaleDateString()}`, 14, 30);

    const tableColumn = [
      "S.No", "Cashier", "Start Time", "End Time", "Duration", 
      "Sales", "Trans", "Cash", "UPI", "Card", "QR", "Non-Cash", "Items", "Status"
    ];
    
    const tableRows = [];

    cashierSummaries.forEach((summary, index) => {
      const cashierDisplay = `${summary.cashier_display || summary.cashier_name || 'N/A'} (ID: ${summary.cashier_uname || summary.cashier_id || 'N/A'})`;
      tableRows.push([
        index + 1,
        cashierDisplay,
        formatTime(summary.session_start_time),
        formatTime(summary.session_end_time),
        formatDuration(summary.session_start_time, summary.session_end_time),
        parseFloat(summary.total_sales || 0).toFixed(2),
        summary.total_transactions || 0,
        parseFloat(summary.cash_total || 0).toFixed(2),
        parseFloat(summary.upi_total || 0).toFixed(2),
        parseFloat(summary.card_total || 0).toFixed(2),
        parseFloat(summary.qr_total || 0).toFixed(2),
        parseFloat(summary.non_cash_total || 0).toFixed(2),
        summary.total_items_sold || 0,
        (summary.status || 'open').toUpperCase()
      ]);
    });

    // Add total row
    tableRows.push([
      { content: "TOTALS:", colSpan: 5, styles: { halign: "right", fontStyle: "bold" } },
      totals.totalSales.toFixed(2),
      totals.totalTransactions,
      totals.totalCash.toFixed(2),
      totals.totalUpi.toFixed(2),
      totals.totalCard.toFixed(2),
      totals.totalQr.toFixed(2),
      totals.totalNonCash.toFixed(2),
      totals.totalItemsSold,
      `${totals.totalSessions} Sessions`
    ]);

    doc.autoTable({
      startY: 40,
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
    });

    doc.save(`Cashier_Summary_Report_${selectedDate}.pdf`);
    toast.success('PDF exported successfully!');
  };

  // Export to Excel
  const exportToExcel = () => {
    const totals = calculateColumnTotals();
    
    const excelData = [
      ['S.No', 'Cashier Name', 'Start Time', 'End Time', 'Duration', 'Total Sales', 'Transactions', 'Cash', 'UPI', 'Card', 'QR', 'Non-Cash', 'Items', 'Status'],
      
      ...cashierSummaries.map((summary, index) => [
        index + 1,
        `${summary.cashier_display || summary.cashier_name || 'N/A'} (ID: ${summary.cashier_uname || summary.cashier_id || 'N/A'})`,
        formatTime(summary.session_start_time),
        formatTime(summary.session_end_time),
        formatDuration(summary.session_start_time, summary.session_end_time),
        parseFloat(summary.total_sales || 0).toFixed(2),
        summary.total_transactions || 0,
        parseFloat(summary.cash_total || 0).toFixed(2),
        parseFloat(summary.upi_total || 0).toFixed(2),
        parseFloat(summary.card_total || 0).toFixed(2),
        parseFloat(summary.qr_total || 0).toFixed(2),
        parseFloat(summary.non_cash_total || 0).toFixed(2),
        summary.total_items_sold || 0,
        (summary.status || 'open').toUpperCase()
      ]),
      
      ['', '', '', '', 'TOTALS:', totals.totalSales.toFixed(2), totals.totalTransactions, totals.totalCash.toFixed(2), 
       totals.totalUpi.toFixed(2), totals.totalCard.toFixed(2), totals.totalQr.toFixed(2), 
       totals.totalNonCash.toFixed(2), totals.totalItemsSold, `${totals.totalSessions} Sessions`]
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    ws['!cols'] = [
      { wch: 8 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
      { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Cashier Summary');
    XLSX.writeFile(wb, `Cashier_Summary_Report_${selectedDate}.xlsx`);
    
    toast.success('Excel file exported successfully!');
  };

  // Print function
  const handlePrint = () => {
    const totals = calculateColumnTotals();
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cashier Summary Report - ${new Date(selectedDate).toLocaleDateString()}</title>
          <style>
            @page {
              size: landscape;
              margin: 15mm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 11px;
              line-height: 1.4;
              color: #000;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 2px solid #007bff;
            }
            .company-name {
              font-size: 20px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 5px;
            }
            .report-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 8px;
            }
            .date {
              font-size: 12px;
              color: #666;
            }
            .summary-stats {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 25px;
            }
            .stat-card {
              border: 1px solid #ddd;
              padding: 12px;
              border-radius: 5px;
              text-align: center;
              background: #f8f9fa;
            }
            .stat-label {
              font-size: 10px;
              color: #666;
              margin-bottom: 5px;
            }
            .stat-value {
              font-size: 15px;
              font-weight: bold;
              color: #007bff;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              font-size: 10px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px 6px;
              text-align: left;
            }
            th {
              background-color: #007bff;
              color: white;
              font-weight: bold;
              font-size: 10px;
            }
            tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .totals-row {
              background-color: #28a745 !important;
              color: white !important;
              font-weight: bold;
            }
            .totals-row td {
              background-color: #28a745 !important;
              color: white !important;
              font-weight: bold;
            }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .status-badge {
              padding: 3px 8px;
              border-radius: 3px;
              font-size: 9px;
              font-weight: bold;
            }
            .status-closed {
              background-color: #28a745;
              color: white;
            }
            .status-open {
              background-color: #ffc107;
              color: #000;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 9px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 15px;
            }
            @media print {
              body { margin: 0; }
              .summary-stats { grid-template-columns: repeat(2, 1fr); }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">View Park</div>
            <div class="report-title">Cashier Summary Report</div>
            <div class="date">Date: ${new Date(selectedDate).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</div>
          </div>

          <div class="summary-stats">
            <div class="stat-card">
              <div class="stat-label">Total Sales</div>
              <div class="stat-value">${formatCurrency(totals.totalSales)}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Total Sessions</div>
              <div class="stat-value">${totals.totalSessions}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Total Transactions</div>
              <div class="stat-value">${totals.totalTransactions}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Total Items</div>
              <div class="stat-value">${totals.totalItemsSold}</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Cashier Name</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Duration</th>
                <th>Total Sales</th>
                <th>Transactions</th>
                <th>Cash</th>
                <th>UPI</th>
                <th>Card</th>
                <th>QR</th>
                <th>Non-Cash</th>
                <th>Items</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${cashierSummaries.map((summary, index) => `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>${summary.cashier_display || summary.cashier_name || 'N/A'}<br><small style="color: #666;">ID: ${summary.cashier_uname || summary.cashier_id || 'N/A'}</small></td>
                  <td class="text-center">${formatTime(summary.session_start_time)}</td>
                  <td class="text-center">${formatTime(summary.session_end_time)}</td>
                  <td class="text-center">${formatDuration(summary.session_start_time, summary.session_end_time)}</td>
                  <td class="text-right">${formatCurrency(summary.total_sales)}</td>
                  <td class="text-center">${summary.total_transactions || 0}</td>
                  <td class="text-right">${formatCurrency(summary.cash_total)}</td>
                  <td class="text-right">${formatCurrency(summary.upi_total)}</td>
                  <td class="text-right">${formatCurrency(summary.card_total)}</td>
                  <td class="text-right">${formatCurrency(summary.qr_total)}</td>
                  <td class="text-right">${formatCurrency(summary.non_cash_total)}</td>
                  <td class="text-center">${summary.total_items_sold || 0}</td>
                  <td class="text-center">
                    <span class="status-badge status-${summary.status === 'closed' ? 'closed' : 'open'}">
                      ${(summary.status || 'open').toUpperCase()}
                    </span>
                  </td>
                </tr>
              `).join('')}
              <tr class="totals-row">
                <td colspan="5" class="text-right"><strong>TOTALS:</strong></td>
                <td class="text-right"><strong>${formatCurrency(totals.totalSales)}</strong></td>
                <td class="text-center"><strong>${totals.totalTransactions}</strong></td>
                <td class="text-right"><strong>${formatCurrency(totals.totalCash)}</strong></td>
                <td class="text-right"><strong>${formatCurrency(totals.totalUpi)}</strong></td>
                <td class="text-right"><strong>${formatCurrency(totals.totalCard)}</strong></td>
                <td class="text-right"><strong>${formatCurrency(totals.totalQr)}</strong></td>
                <td class="text-right"><strong>${formatCurrency(totals.totalNonCash)}</strong></td>
                <td class="text-center"><strong>${totals.totalItemsSold}</strong></td>
                <td class="text-center"><strong>${totals.totalSessions} Sessions</strong></td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Report generated by Wheel POS System</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  // Thermal Print function
  const printThermalReceipt = () => {
    const totals = calculateColumnTotals();
    const printWindow = window.open('', '_blank');
    
    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cashier Summary - ${new Date(selectedDate).toLocaleDateString()}</title>
        <meta charset="UTF-8">
        <style>
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 13px;
            line-height: 1.4;
            width: 80mm;
            margin: 0;
            padding: 5mm;
            background: white;
            font-weight: 500;
          }
          
          .receipt-header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 8px;
            margin-bottom: 12px;
          }
          
          .receipt-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 3px;
            letter-spacing: 0.5px;
          }
          
          .receipt-subtitle {
            font-size: 14px;
            margin-bottom: 6px;
            font-weight: 600;
          }
          
          .section {
            margin-bottom: 10px;
            padding-bottom: 5px;
          }
          
          .section-title {
            font-weight: bold;
            text-decoration: underline;
            margin-bottom: 5px;
            font-size: 14px;
          }
          
          .info-line {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
            font-size: 13px;
          }
          
          .separator {
            border-top: 1px dashed #000;
            margin: 8px 0;
          }
          
          .total-line {
            font-weight: bold;
            font-size: 15px;
          }
          
          .cashier-row {
            border: 1px solid #333;
            padding: 6px;
            margin: 6px 0;
            background: #f5f5f5;
          }
          
          .cashier-name {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 4px;
          }
          
          .footer {
            text-align: center;
            margin-top: 12px;
            font-size: 11px;
          }
        </style>
      </head>
      <body>
        <div class="receipt-header">
          <div class="receipt-title">🎡 THE VIEW PATTAYA</div>
          <div class="receipt-subtitle">CASHIER SUMMARY REPORT</div>
          <div style="font-size: 13px;">Date: ${new Date(selectedDate).toLocaleDateString()}</div>
          <div style="font-size: 13px;">Time: ${new Date().toLocaleTimeString()}</div>
        </div>

        <div class="section">
          <div class="section-title">OVERALL SUMMARY</div>
          <div class="info-line">
            <span>Total Sessions:</span>
            <span><strong>${totals.totalSessions}</strong></span>
          </div>
          <div class="info-line total-line">
            <span>Total Sales:</span>
            <span>${formatCurrency(totals.totalSales)}</span>
          </div>
          <div class="info-line">
            <span>Total Transactions:</span>
            <span><strong>${totals.totalTransactions}</strong></span>
          </div>
          <div class="info-line">
            <span>Total Items:</span>
            <span><strong>${totals.totalItemsSold}</strong></span>
          </div>
        </div>

        <div class="separator"></div>

        <div class="section">
          <div class="section-title">PAYMENT TOTALS</div>
          <div class="info-line">
            <span>Cash:</span>
            <span><strong>${formatCurrency(totals.totalCash)}</strong></span>
          </div>
          <div class="info-line">
            <span>Card:</span>
            <span><strong>${formatCurrency(totals.totalCard)}</strong></span>
          </div>
          <div class="info-line">
            <span>UPI:</span>
            <span><strong>${formatCurrency(totals.totalUpi)}</strong></span>
          </div>
          <div class="info-line">
            <span>QR:</span>
            <span><strong>${formatCurrency(totals.totalQr)}</strong></span>
          </div>
          <div class="info-line">
            <span>Non-Cash:</span>
            <span><strong>${formatCurrency(totals.totalNonCash)}</strong></span>
          </div>
        </div>

        <div class="separator"></div>

        <div class="section">
          <div class="section-title">CASHIER SESSIONS</div>
          ${cashierSummaries.map((summary, index) => `
            <div class="cashier-row">
              <div class="cashier-name">${index + 1}. ${summary.cashier_display || summary.cashier_name || 'N/A'} (ID: ${summary.cashier_uname || summary.cashier_id || 'N/A'})</div>
              <div class="info-line" style="font-size: 12px;">
                <span>Time:</span>
                <span>${formatTime(summary.session_start_time)} - ${formatTime(summary.session_end_time)}</span>
              </div>
              <div class="info-line" style="font-size: 12px;">
                <span>Duration:</span>
                <span>${formatDuration(summary.session_start_time, summary.session_end_time)}</span>
              </div>
              <div class="info-line total-line">
                <span>Sales:</span>
                <span>${formatCurrency(summary.total_sales)}</span>
              </div>
              <div class="info-line">
                <span>Transactions:</span>
                <span><strong>${summary.total_transactions || 0}</strong></span>
              </div>
              <div class="info-line">
                <span>Items:</span>
                <span><strong>${summary.total_items_sold || 0}</strong></span>
              </div>
              <div class="info-line" style="font-size: 12px;">
                <span>Cash: ${formatCurrency(summary.cash_total)}</span>
                <span>Card: ${formatCurrency(summary.card_total)}</span>
              </div>
              <div class="info-line" style="font-size: 12px;">
                <span>UPI: ${formatCurrency(summary.upi_total)}</span>
                <span>QR: ${formatCurrency(summary.qr_total)}</span>
              </div>
              <div class="info-line">
                <span>Status:</span>
                <span><strong>${(summary.status || 'open').toUpperCase()}</strong></span>
              </div>
            </div>
          `).join('')}
        </div>

        <div class="footer">
          <div class="separator"></div>
          <div>Generated: ${new Date().toLocaleString()}</div>
          <div>System: View Park v2.0</div>
          <div style="font-weight: bold; margin-top: 4px;">** End of Report **</div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
      // Fallback: Close window after 1 second if onafterprint doesn't fire
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    };
  };

  const totals = calculateColumnTotals();

  return (
    <Layout>
      <div className="cashier-summary-report-container">
        <ToastContainer />
        
        <div className="page-header d-flex justify-content-between align-items-center mb-4">
          <div>
            <h6 className="page-title">
              <FaUser className="me-2" />
              Cashier Summary Report
            </h6>
            <p className="text-muted">View cashier session summaries and export reports</p>
          </div>
          
          <div className="header-actions d-flex gap-2">
            <Form.Control
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-picker"
              style={{ width: '160px' }}
            />
            
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={exportToPDF}
              disabled={loading || cashierSummaries.length === 0}
              startIcon={<FaFilePdf />}
            >
              PDF
            </Button>

            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={exportToExcel}
              disabled={loading || cashierSummaries.length === 0}
              startIcon={<FaFileExcel />}
            >
              Excel
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handlePrint}
              disabled={loading || cashierSummaries.length === 0}
              startIcon={<FaPrint />}
            >
              Print
            </Button>
            
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={printThermalReceipt}
              disabled={loading || cashierSummaries.length === 0}
              startIcon={<FaPrint />}
            >
              🧾 Thermal
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <CircularProgress size={60} />
            <p className="mt-3">Loading cashier summary data...</p>
          </div>
        ) : (
          <Card>
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <DateRange className="me-2" />
                <span>Cashier Summaries for {new Date(selectedDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <span className="">{cashierSummaries.length} Sessions</span>
            </Card.Header>
            <Card.Body>
              {cashierSummaries.length === 0 ? (
                <Alert variant="info" className="text-center">
                  <FaCalendarAlt className="mb-2" size={40} />
                  <p>No cashier summaries found for the selected date.</p>
                  <small>Select a different date or check if any cashier sessions were recorded.</small>
                </Alert>
              ) : (
                <>
                  {/* Summary Statistics */}
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card className="border-primary">
                        <Card.Body className="text-center">
                          <h6 className="text-primary">Total Sales</h6>
                          <h4 className="text-success">{formatCurrency(totals.totalSales)}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-info">
                        <Card.Body className="text-center">
                          <h6 className="text-info">Total Sessions</h6>
                          <h4 className="text-primary">{totals.totalSessions}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-warning">
                        <Card.Body className="text-center">
                          <h6 className="text-warning">Total Transactions</h6>
                          <h4 className="text-dark">{totals.totalTransactions}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-success">
                        <Card.Body className="text-center">
                          <h6 className="text-success">Items Sold</h6>
                          <h4 className="text-primary">{totals.totalItemsSold}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Detailed Table */}
                  <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch', marginTop: '20px' }}>
                    <Table striped bordered hover style={{ minWidth: '1400px', marginBottom: 0 }}>
                      <thead className="table-primary">
                        <tr>
                          <th style={{ whiteSpace: 'nowrap' }}>S.No</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Cashier Name</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Start Time</th>
                          <th style={{ whiteSpace: 'nowrap' }}>End Time</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Duration</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Total Sales</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Transactions</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Cash</th>
                          <th style={{ whiteSpace: 'nowrap' }}>UPI</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Card</th>
                          <th style={{ whiteSpace: 'nowrap' }}>QR</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Non-Cash</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Items</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                        </tr>
                      </thead>
                    <tbody>
                      {cashierSummaries.map((summary, index) => (
                        <tr key={summary.id || index}>
                          <td className="text-center">{index + 1}</td>
                          <td>
                            <div>{summary.cashier_display || summary.cashier_name || 'N/A'}</div>
                            <small className="text-muted">ID: {summary.cashier_uname || summary.cashier_id || 'N/A'}</small>
                          </td>
                          <td className="text-center">{formatTime(summary.session_start_time)}</td>
                          <td className="text-center">{formatTime(summary.session_end_time)}</td>
                          <td className="text-center">{formatDuration(summary.session_start_time, summary.session_end_time)}</td>
                          <td className="text-end">{formatCurrency(summary.total_sales)}</td>
                          <td className="text-center">{summary.total_transactions || 0}</td>
                          <td className="text-end">{formatCurrency(summary.cash_total)}</td>
                          <td className="text-end">{formatCurrency(summary.upi_total)}</td>
                          <td className="text-end">{formatCurrency(summary.card_total)}</td>
                          <td className="text-end">{formatCurrency(summary.qr_total)}</td>
                          <td className="text-end">{formatCurrency(summary.non_cash_total)}</td>
                          <td className="text-center">{summary.total_items_sold || 0}</td>
                          <td className="text-center">
                            <span className={`badge bg-${summary.status === 'closed' ? 'success' : 'warning'}`}>
                              {(summary.status || 'open').toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="table-success">
                        <td colSpan="5" className="text-end"><strong>TOTALS:</strong></td>
                        <td className="text-end"><strong>{formatCurrency(totals.totalSales)}</strong></td>
                        <td className="text-center"><strong>{totals.totalTransactions}</strong></td>
                        <td className="text-end"><strong>{formatCurrency(totals.totalCash)}</strong></td>
                        <td className="text-end"><strong>{formatCurrency(totals.totalUpi)}</strong></td>
                        <td className="text-end"><strong>{formatCurrency(totals.totalCard)}</strong></td>
                        <td className="text-end"><strong>{formatCurrency(totals.totalQr)}</strong></td>
                        <td className="text-end"><strong>{formatCurrency(totals.totalNonCash)}</strong></td>
                        <td className="text-center"><strong>{totals.totalItemsSold}</strong></td>
                        <td className="text-center"><strong>{totals.totalSessions} Sessions</strong></td>
                      </tr>
                    </tbody>
                    </Table>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

    
      </div>
    </Layout>
  );
}
